/**
 * SupplierURLField Component
 * 
 * URL input field for Supplier Reorder URL with validation.
 * 
 * Requirements: 3.7
 */

import { useState, useCallback } from 'react';

export interface SupplierURLFieldProps {
  /** Current value */
  value: string;
  /** Called when value changes */
  onChange: (value: string) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
}

/**
 * Validate if a string is a valid URL
 */
export function isValidURL(url: string): boolean {
  if (!url.trim()) {
    return true; // Empty is valid (optional field)
  }
  
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function SupplierURLField({
  value,
  onChange,
  disabled = false,
}: SupplierURLFieldProps) {
  const [touched, setTouched] = useState(false);
  
  const isValid = isValidURL(value);
  const showError = touched && !isValid;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setTouched(true);
  }, []);

  return (
    <>
      <input
        type="url"
        id="supplierReorderUrl"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="https://supplier.com/product/..."
        disabled={disabled}
        aria-invalid={showError}
        aria-describedby={showError ? 'supplierUrlError' : undefined}
      />
      {showError && (
        <span id="supplierUrlError" className="field-error" role="alert">
          Please enter a valid URL (http:// or https://)
        </span>
      )}
    </>
  );
}

export default SupplierURLField;
